import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    whatsapp_notifications: false
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`, {
        withCredentials: true
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${BACKEND_URL}/api/customers/${editingCustomer.customer_id}`, formData, {
          withCredentials: true
        });
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customers`, formData, {
          withCredentials: true
        });
        toast.success('Customer created successfully');
      }
      fetchCustomers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save customer');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/customers/${customerId}`, {
        withCredentials: true
      });
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || '',
      whatsapp_notifications: customer.whatsapp_notifications || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({ name: '', phone: '', email: '', address: '', whatsapp_notifications: false });
  };

  if (loading) {
    return <div className="text-stone-600">Loading customers...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Customers
          </h1>
          <p className="mt-2 text-base text-stone-600">Manage your customer database</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-customer-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="customer-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="customer-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="customer-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="customer-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="border-stone-200 focus:ring-amber-700"
                />
              </div>
              <div className="flex items-center justify-between py-2 px-1 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <Bell className="w-3.5 h-3.5 text-green-700" />
                  <Label className="text-xs text-green-800 font-medium cursor-pointer">WhatsApp Notifications</Label>
                </div>
                <Switch data-testid="customer-whatsapp-toggle" checked={formData.whatsapp_notifications} onCheckedChange={v => setFormData({ ...formData, whatsapp_notifications: v })} />
              </div>
              <Button type="submit" data-testid="submit-customer-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                {editingCustomer ? 'Update Customer' : 'Create Customer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Name</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Phone</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Email</th>
                <th className="text-left px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Address</th>
                <th className="text-right px-6 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    No customers found. Create your first customer to get started.
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.customer_id} data-testid={`customer-row-${customer.customer_id}`} className="hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-stone-900">{customer.name}</td>
                    <td className="px-6 py-4 text-stone-700 tabular-nums">
                      {customer.phone}
                      {customer.whatsapp_notifications && <Bell data-testid={`customer-whatsapp-bell-${customer.customer_id}`} className="w-3 h-3 inline ml-1.5 text-green-600" />}
                    </td>
                    <td className="px-6 py-4 text-stone-700">{customer.email || '-'}</td>
                    <td className="px-6 py-4 text-stone-700">{customer.address || '-'}</td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        data-testid={`edit-customer-${customer.customer_id}`}
                        onClick={() => openEditDialog(customer)}
                        className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 transition-colors duration-200"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-customer-${customer.customer_id}`}
                        onClick={() => handleDelete(customer.customer_id)}
                        className="inline-flex items-center gap-1 text-red-700 hover:text-red-800 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Customers;